import {formatDate} from "../store/reducers/order.reducer";
import * as moment from 'moment';

export function getFileNameFromHttpResponse(httpResponse) {
	const contentDispositionHeader = httpResponse.headers.get('Content-Disposition');
	const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
	return result.replace(/"/g, '').replace("UTF-8''", "").replace(/%2C/g, ",");
}

export function getCookie(name: string) {
	const ca: Array<string> = document.cookie.split(';');
	const caLen: number = ca.length;
	const cookieName = `${name}=`;
	let c: string;

	for (let i: number = 0; i < caLen; i += 1) {
		c = ca[i].replace(/^\s+/g, '');
		if (c.indexOf(cookieName) === 0) {
			return c.substring(cookieName.length, c.length);
		}
	}
	return '';
}

export function cookieExists(name: string): boolean {
	return !!document.cookie
		.split('; ')
		.find((row) => {
			if(row.startsWith('_ga_')) return false; // Google Analytics cookie _ga_ID that cannot be removed
			return row.startsWith(name);
		});
}

export function expireAll(cookiesNames: string[], domain: string = 'localhost') {
	cookiesNames.forEach(cookie => {
		expireCookie(cookie, domain);
	});
}

export function expireCookie(name: string, domain: string): void {
	const cookies: string[] = document.cookie
		.split('; ')

	cookies.forEach(cookie => {
		if (cookie.startsWith(name)) {
			document.cookie = `${cookie.substring(0, cookie.indexOf('='))}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
		}
	});
}

export function setCookie(name: string, value: string, expireDays: number, path: string = '') {
	const d: Date = new Date();
	d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
	const expires: string = `expires=${d.toUTCString()}`;
	const cpath: string = path ? `; path=${path}` : '';
	document.cookie = `${name}=${value}; ${expires}${cpath}`;
}

export function sanitizeDate(date: Date | string | moment.Moment): Date | null {
	if (date != null) {
		if (moment.isMoment(date)) {
			return date.toDate();
		} else {
			date = typeof date === 'string' ? new Date(date) : date;
			return date.getTime() === new Date(null).getTime() ? null : date;
		}
	}

	return null;
}

export const COOKIES_ACCEPTED: string = "COOKIES_ACCEPTED";


function isObject(obj) {
	const type = typeof obj;
	return type === 'object' && !!obj;
}

export function deepCopy(src: any): any {
	if (typeof src === 'string') {
		return src;
	}

	const target = {};
	for (const prop in src) {
		if (src.hasOwnProperty(prop)) {
			if (Array.isArray(src[prop])) {
				const arr: any[] = [];
				(src[prop] as Array<any>).forEach((value, index) => arr[index] = deepCopy(value));
				target[prop] = arr;
			} else if (src[prop] instanceof Date) {
				target[prop] = src[prop];
			} else if (isObject(src[prop])) {
				target[prop] = deepCopy(src[prop]);
			} else {
				target[prop] = src[prop];
			}
		}
	}
	return target;
}

function deepEqualArrays(arr1: any[], arr2: any[], excludedProperties: string[]) {
	if (arr1.length !== arr2.length) {
		return false;
	}

	let arrayEquals: boolean = true;
	// tslint:disable-next-line:forin
	for (const i in arr1) {
		arrayEquals = arrayEquals && deepEqual(arr1[i], arr2[i], excludedProperties);
	}

	return arrayEquals;
}

function deepEqualObjects(obj1: any, obj2: any, excludedProperties: string[]) {
	if (Array.isArray(obj1) && Array.isArray(obj2)) {
		return deepEqualArrays(obj1 as Array<any>, obj2 as Array<any>, excludedProperties);
	} else {
		let objectsEqual: boolean = true;

		for (const prop in obj1) {
			if (obj1.hasOwnProperty(prop) && obj2.hasOwnProperty(prop)) {
				if (excludedProperties.findIndex(value => value === prop) >= 0) {
					// skip
				} else if (Array.isArray(obj1[prop])) {
					objectsEqual = objectsEqual && deepEqualArrays(obj1[prop] as Array<any>, obj2[prop] as Array<any>, excludedProperties);
				} else {
					objectsEqual = objectsEqual && deepEqual(obj1[prop], obj2[prop], excludedProperties);
				}
			} else {
				objectsEqual = false;
				break;
			}
		}

		return objectsEqual;
	}
}

// Conditions for checking date object with formatted date string
function checkDateWithFormattedDate(obj1: any, obj2: any) {
	if (Object.prototype.toString.call(obj1) === '[object Date]') {
		return formatDate(obj1) == obj2;
	} else if (Object.prototype.toString.call(obj2) === '[object Date]') {
		return formatDate(obj2) == obj1;
	}
	return false;
}

export function deepEqual(obj1: any, obj2: any, excludedProperties: string[] = []): boolean {
	const type = typeof obj1;
	if (type !== typeof obj2) {
		return checkDateWithFormattedDate(obj1, obj2);
	} else if (type === "object") {
		return deepEqualObjects(obj1, obj2, excludedProperties);
	} else {
		return obj1 === obj2;
	}
}

export function groupBy(data, keyFn) {
	return data.reduce((agg, item) => {
		const group = keyFn(item);
		agg[group] = [...(agg[group] || []), item];
		return agg;
	}, {});
}
